import React, { useCallback, useEffect, useState } from 'react';
import { Topbar, CalendarModal } from '@components';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
    Radio,
    Spin,
} from 'antd';
import {
    CalendarFilled,
    NoResultOutlined,
} from '@assets';
import { PRIVATE_ROUTE, PUBLIC_ROUTE } from '@router/route.constants';
import { api } from '@api';
import {
    LIMITS,
    TOPUP_SK,
    WITHDRAW_SK,
    BONUS_SK,
} from '@constants';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useTranslation } from 'react-i18next';
import {
    ListContainer,
} from '../Wallet/Wallet.style';
import TransactionListItem from '../Wallet/TransactionListItem/TransactionListItem';

const RootContainer = styled.div`
padding-bottom: 16px;
height: calc(100vh - 86px);
overflow: hidden;

.anticon .anticon-calendar {
    font-size: 28px;
    color: #fff;
}
`;

const RadioGroup = styled(Radio.Group)`
width: calc(100% - 44px);
display: grid;
grid-template-columns: 25% 25% 25% 25%;
gap: 4px;
margin: 16px;

  .ant-radio-wrapper span.ant-radio + * {
    padding-inline-start: 0px !important;
    padding-inline-end: 0 !important;
  }

  @media screen and (max-width: 355px) {
    .ant-radio-wrapper span.ant-radio + * {
        padding-inline-end: 0 !important;
    }
  }
`;

const RadioButton = styled(Radio)`
display: flex !important;
padding: 8px;
border-radius: 4px;
text-align: center;
justify-content: center;
align-items: center;
margin-inline-end: 0 !important;
background-color: ${(props) =>
        props.status
            ? "#D66700"
            : "#fff"};

.ant-radio {
    opacity: 0;
    position: fixed;
    width: 0;
  }

  @media screen and (max-width: 375px) {
    padding: 4px;
  }

  @media screen and (max-width: 330px) {
    padding: 4px;
  }
`;

const Text = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  font-size: 16px;
  font-weight: 700;
  color: ${(props) =>
        props.status
            ? "#fff"
            : "#C2C2C2"};

  @media screen and (max-width: 375px) {
    font-size: 14px;
  }

  @media screen and (max-width: 330px) {
    font-size: 12px;
  }
`;

const StyledListWrapper = styled(ListContainer).attrs({
    id: 'scrollable-div',
})`
height: calc(100vh - 143px);
`;

const SpinWrapper = styled.div`
padding-top: 12px;
text-align: center;
`;

export const EmptyBox = styled.div`
	width: 100%;
	height: calc(100vh - 143px);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;
`;

export const BoldText = styled.div`
padding-top: 16px;
font-size: 16px;
font-weight: 700;
color: #2A2A2A;
line-height: 1.5;
`;

export const NoResultText = styled.div`
	padding-top: 8px;
	font-size: 16px;
	font-style: normal;
	font-weight: 400;
	line-height: normal;
	color: #5D5189;
`;

const {
    DEFAULT_PAGE,
    PAGE_SIZE,
} = LIMITS;

const apiDateFormat = 'YYYY-MM-DD';

const TransactionHistory = () => {
    const { t } = useTranslation()
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [type, setType] = useState('');
    const [page, setPage] = useState(DEFAULT_PAGE);
    const [total, setTotal] = useState(0);
    const [open, setOpen] = useState(false);
    const [date, setDate] = useState('');
    const [loading, setLoading] = useState(true);

    let controller = type;

    const getTransactions = useCallback(() => {
        setLoading(true);
        api
            .getTransactions({
                page,
                limit: PAGE_SIZE,
                type,
                start_date: date,
                end_date: date,
            })
            .then(({ transactions, pagination }) => {
                if (controller === type) {
                    setData(prevState => {
                        return [
                            ...prevState,
                            ...transactions,
                        ];
                    });
                    setPage(pagination.page);
                    setTotal(pagination.total);
                    setLoading(false);
                }
            })
            .catch(() => { });

    }, [type, page, date, controller]);

    useEffect(() => {
        getTransactions();

    }, [getTransactions]);

    const onChange = (e) => {
        controller = e.target.value;
        setType(e.target.value);
        setPage(DEFAULT_PAGE);
        setData([]);
        setTotal(0);
        setLoading(true);
    };

    const onOk = (newdate) => {
        newdate = newdate.format(apiDateFormat);
        if (newdate !== date) {
            setDate(newdate);
            setType('');
            setPage(DEFAULT_PAGE);
            setData([]);
            setTotal(0);
        }
        handleClose();
    }

    const onReset = () => {
        if (date) {
            setDate('');
            setType('');
            setPage(DEFAULT_PAGE);
            setData([]);
            setTotal(0);
        }
    }

    const handleClose = () => {
        setOpen(false);
    }

    const goBack = () => {
        navigate(`/${PUBLIC_ROUTE.HOME}/${PRIVATE_ROUTE.WALLET}`);
    }

    const renderTop = () => (
        <RadioGroup onChange={onChange} value={type}>
            <RadioButton value={""} status={type === ""}>
                <Text status={type === ""}>
                    {t("wallet.transactionHistoryHeadingAll")}
                </Text>
            </RadioButton>
            <RadioButton value={TOPUP_SK} status={type === TOPUP_SK}>
                <Text status={type === TOPUP_SK}>
                    {t("wallet.walletTopUp")}
                </Text>
            </RadioButton>
            <RadioButton value={WITHDRAW_SK} status={type === WITHDRAW_SK}>
                <Text status={type === WITHDRAW_SK}>
                    {t("wallet.walletWithdraw")}
                </Text>
            </RadioButton>
            <RadioButton value={BONUS_SK} status={type === BONUS_SK}>
                <Text status={type === BONUS_SK}>
                    {t("wallet.transactionHistoryHeadingBonus")}
                </Text>
            </RadioButton>
        </RadioGroup>
    );

    const Loader = () => (
        <SpinWrapper>
            <Spin />
        </SpinWrapper>
    );

    const EmptyCard = () => (
        <EmptyBox>
            <NoResultOutlined />
            <NoResultText>{t("common.noRecord")}</NoResultText>
        </EmptyBox>
    );

    return (
        <>
            <Topbar
                title={t("wallet.transactionHistoryTitle")}
                rightIcon={
                    <CalendarFilled onClick={() => setOpen(true)} />
                }
                goBack={goBack}
            />
            <RootContainer>
                {renderTop()}
                <StyledListWrapper>
                    <InfiniteScroll
                        dataLength={data.length}
                        next={() => setPage((prev) => prev + 1)}
                        hasMore={loading || data.length < total}
                        loader={<Loader />}
                        scrollableTarget='scrollable-div'
                        endMessage={!loading && data.length === 0 && <EmptyCard />}
                    >
                        {data.map((item, key) => (
                            <TransactionListItem key={key} item={item} />
                        ))}
                    </InfiniteScroll>
                </StyledListWrapper>
            </RootContainer>
            <CalendarModal
                open={open}
                date={date}
                onOk={onOk}
                onReset={onReset}
                handleClose={handleClose}
            />
        </>
    );
}

export default TransactionHistory;